import { Routes } from '@angular/router';
import { AuthGuard } from '@auth0/auth0-angular';
import { AccessDeniedRiComponent, HomeComponent } from '@r1-development/rps-shared-ui';
import { unsavedChangesGuard } from './health-system-admin/guards/unsaved-changes.guard';
import { featureGuard } from './shared/feature-access/feature.guard';

export const routes: Routes = [
  {
    path: '',
    component: HomeComponent,
    canActivate: [AuthGuard],
    data: {
      title: 'Pricing',
      showPageHeader: true,
      redirectTo: '/monitoring',
    },
  },
  {
    path: 'access-denied',
    component: AccessDeniedRiComponent,
    canActivate: [AuthGuard],
    data: { showPageHeader: true },
  },
  {
    path: 'monitoring',
    canActivate: [AuthGuard],
    title: 'Pricing Monitoring',
    loadComponent: () =>
      import('../app/admin/components/dashboard/dashboard.component').then((m) => m.DashboardComponent),
  },
  {
    path: 'monitoring/job/:jobId/:consumerId',
    canActivate: [AuthGuard],
    title: 'Pricing Job Details',
    loadComponent: () =>
      import('../app/admin/components/job-details-page/job-details-page.component').then((m) => m.JobDetailsPageComponent),
  },
  {
    path: 'orchestration',
    canActivate: [AuthGuard],
    title: 'Orchestration Monitoring',
    loadComponent: () =>
      import('../app/orchestration/components/orchestration-dashboard/orchestration-dashboard.component').then(
        (m) => m.OrchestrationDashboardComponent
      ),
  },
  {
    path: 'onboarding',
    canActivate: [AuthGuard],
    title: 'Onboarding',
    loadComponent: () =>
      import('../app/onboarding/components/onboarding-dashboard/onboarding-dashboard.component').then(
        (m) => m.OnboardingDashboardComponent
      ),
  },
  {
    path: 'health-system-admin',
    canActivate: [AuthGuard],
    canDeactivate: [unsavedChangesGuard],
    title: 'Health System Super Admin',
    loadComponent: () =>
      import(
        '../app/health-system-admin/components/health-system-admin-dashboard/health-system-admin-dashboard.component'
      ).then((m) => m.HealthSystemAdminDashboardComponent),
  },
  {
    path: 'health-system-admin/:healthSystemId/:providerTypeId/edit',
    canActivate: [AuthGuard],
    canDeactivate: [unsavedChangesGuard],
    title: 'Edit Health System',
    loadComponent: () =>
      import(
        '../app/health-system-admin/components/health-system-edit-page/health-system-edit-page.component'
      ).then((m) => m.HealthSystemEditPageComponent),
  },
  {
    // Legacy 2-segment route (back-compat for existing bookmarks). Edit component treats
    // providerTypeId as optional and only 409s when the HS has multiple provider rows.
    path: 'health-system-admin/:healthSystemId/edit',
    canActivate: [AuthGuard],
    canDeactivate: [unsavedChangesGuard],
    title: 'Edit Health System',
    loadComponent: () =>
      import(
        '../app/health-system-admin/components/health-system-edit-page/health-system-edit-page.component'
      ).then((m) => m.HealthSystemEditPageComponent),
  },
  {
    path: 'test-population',
    canActivate: [AuthGuard],
    title: 'Targeted Test Run',
    loadComponent: () =>
      import('../app/test-population/components/test-population-page/test-population-page.component').then(
        (m) => m.TestPopulationPageComponent
      ),
  },
  {
    path: 'pricing-search',
    canActivate: [AuthGuard],
    title: 'Pricing Search',
    loadComponent: () =>
      import('../app/pricing-search/components/pricing-search/pricing-search.component').then(
        (m) => m.PricingSearchComponent
      ),
  },
  {
    path: 'coverage',
    canActivate: [AuthGuard],
    title: 'Pricing Coverage',
    loadComponent: () =>
      import('../app/coverage/components/coverage-dashboard/coverage-dashboard.component').then(
        (m) => m.CoverageDashboardComponent
      ),
  },
  {
    // Full-page host for the details drawer (the drawer's "Open in tab" target) — any dimension
    // entity: /coverage/detail/Payer/AETNA, /coverage/detail/Facility/123, etc.
    path: 'coverage/detail/:dimension/:key',
    canActivate: [AuthGuard],
    title: 'Pricing Coverage — Details',
    loadComponent: () =>
      import('../app/coverage/components/coverage-details-page/coverage-details-page.component').then(
        (m) => m.CoverageDetailsPageComponent
      ),
  },
  {
    // Legacy Health-System detail route (back-compat for existing/bookmarked links).
    path: 'coverage/hs/:hsId',
    canActivate: [AuthGuard],
    title: 'Pricing Coverage — Health System',
    loadComponent: () =>
      import('../app/coverage/components/coverage-details-page/coverage-details-page.component').then(
        (m) => m.CoverageDetailsPageComponent
      ),
  },
  {
    path: 'accuracy',
    canActivate: [AuthGuard],
    title: 'Pricing Accuracy',
    loadComponent: () =>
      import('../app/accuracy/components/accuracy-dashboard/accuracy-dashboard.component').then(
        (m) => m.AccuracyDashboardComponent
      ),
  },
  {
    path: 'accuracy/consumers',
    canActivate: [AuthGuard],
    title: 'Pricing Accuracy — Methodologies',
    loadComponent: () =>
      import('../app/accuracy/components/accuracy-consumers/accuracy-consumers.component').then(
        (m) => m.AccuracyConsumersComponent
      ),
  },
  {
    // Full-page account view (the account drawer's "Open in New Tab" target) — a shareable deep link:
    // /accuracy/accounts#s=<state> (fragment, so the WAF never sees it; legacy ?s= still read), built by encodeAccountsViewState.
    path: 'accuracy/accounts',
    canActivate: [AuthGuard],
    title: 'Pricing Accuracy — Accounts',
    loadComponent: () =>
      import('../app/accuracy/components/accuracy-accounts-page/accuracy-accounts-page.component').then(
        (m) => m.AccuracyAccountsPageComponent
      ),
  },
  {
    // Full-page detail (the dimension-drill "open in tab" target): /accuracy/detail/Payer/AETNA, etc.
    path: 'accuracy/detail/:dimension/:key',
    canActivate: [AuthGuard],
    title: 'Pricing Accuracy — Details',
    loadComponent: () =>
      import('../app/accuracy/components/accuracy-detail/accuracy-detail.component').then(
        (m) => m.AccuracyDetailComponent
      ),
  },
  {
    // The accuracy agent's blackboard, read-only: contract findings, known issues, working knowledge. The open
    // record rides in the query string (?contract=… / ?issue=… / ?knowledge=…, plus ?tab=).
    path: 'accuracy-analysis',
    canActivate: [AuthGuard, featureGuard('accuracy_analysis')],
    title: 'Accuracy Analysis',
    loadComponent: () =>
      import('../app/accuracy-analysis/components/accuracy-analysis-page/accuracy-analysis-page.component').then(
        (m) => m.AccuracyAnalysisPageComponent
      ),
  },
  {
    // Full-screen viewer for one contract's findings (the findings drawer's "Full screen").
    path: 'accuracy-analysis/contracts/:contractMasterId',
    canActivate: [AuthGuard, featureGuard('accuracy_analysis')],
    title: 'Accuracy Analysis — Contract findings',
    loadComponent: () =>
      import('../app/accuracy-analysis/components/contract-findings-page/contract-findings-page.component').then(
        (m) => m.ContractFindingsPageComponent
      ),
  },
  {
    // The shared Action List viewer (accuracy action tracker, plan §8.2): linkable, gated like /accuracy-analysis.
    path: 'accuracy-analysis/action-lists/:id',
    canActivate: [AuthGuard, featureGuard('accuracy_analysis')],
    title: 'Accuracy Analysis — Action list',
    loadComponent: () =>
      import('../app/accuracy-analysis/components/action-list-page/action-list-page.component').then(
        (m) => m.ActionListPageComponent
      ),
  },
  {
    path: 'pricing-search/accounts/:accountId',
    canActivate: [AuthGuard],
    title: 'Pricing Details',
    loadComponent: () =>
      import('../app/pricing-search/components/pricing-details/pricing-details.component').then(
        (m) => m.PricingDetailsComponent
      ),
  },
  {
    path: 'pricing-search/pb-accounts/:accountId',
    canActivate: [AuthGuard],
    title: 'PB Pricing Details',
    loadComponent: () =>
      import('../app/pricing-search/components/pb-pricing-details/pb-pricing-details.component').then(
        (m) => m.PbPricingDetailsComponent
      ),
  },
  // Admin ▸ Request Consumers / Reporting Consumers. Flat paths matching the rest of the app and the
  // Path values registered in ri-user-management-api (PricingSetup.AdminMenu).
  {
    path: 'request-consumers',
    canActivate: [AuthGuard],
    title: 'Request Consumers',
    loadComponent: () =>
      import('../app/configuration/components/request-consumers/request-consumers.component').then(
        (m) => m.RequestConsumersComponent
      ),
  },
  {
    path: 'request-consumers/:id',
    canActivate: [AuthGuard],
    title: 'Request Consumer',
    loadComponent: () =>
      import('../app/configuration/components/request-consumer-detail/request-consumer-detail.component').then(
        (m) => m.RequestConsumerDetailComponent
      ),
  },
  {
    path: 'reporting-consumers',
    canActivate: [AuthGuard],
    title: 'Reporting Consumers',
    loadComponent: () =>
      import('../app/configuration/components/reporting-consumers/reporting-consumers.component').then(
        (m) => m.ReportingConsumersComponent
      ),
  },
  {
    path: 'reporting-consumers/:code',
    canActivate: [AuthGuard],
    title: 'Reporting Consumer — Clients',
    loadComponent: () =>
      import(
        '../app/configuration/components/reporting-consumer-clients/reporting-consumer-clients.component'
      ).then((m) => m.ReportingConsumerClientsComponent),
  },
  {
    path: 'facility-region-mapping',
    canActivate: [AuthGuard],
    title: 'Facility Region Mapping',
    loadComponent: () =>
      import('../app/facility-region-mapping/components/facility-region-mapping/facility-region-mapping.component').then(
        (m) => m.FacilityRegionMappingComponent
      ),
  },
  {
    // Allow-list admin (pricingdb.feature_allow_list). Deliberately not in the menu: reached by typing /feature-allow-list.
    path: 'feature-allow-list',
    canActivate: [AuthGuard],
    title: 'Feature allow list',
    loadComponent: () =>
      import('../app/feature-allow-list/feature-allow-list-page.component').then((m) => m.FeatureAllowListPageComponent),
  },
  {
    path: 'callback',
    pathMatch: 'full',
    redirectTo: '',
  },
  {
    path: '**',
    redirectTo: '',
  },
];
